import { X, Edit, Download, FileText } from 'lucide-react';

interface TaxReceiptDetailModalProps {
  receipt: any;
  onClose: () => void;
  onEdit: () => void;
}

export function TaxReceiptDetailModal({ receipt, onClose, onEdit }: TaxReceiptDetailModalProps) {
  function getStatusBadge(status: string) {
    const badges = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-700' },
      issued: { label: 'Émis', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-700' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    );
  }

  const totalAmount = Number(receipt.donation_amount) + Number(receipt.donation_nature_amount || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-900">
                  Reçu fiscal {receipt.receipt_number}
                </h3>
                {getStatusBadge(receipt.status)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Créé le {new Date(receipt.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">CERFA 16216*02</p>
                <p className="text-sm text-blue-700">
                  Reçu des dons et versements - Article 238 bis du CGI
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Donateur</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Type :</span>
                <span className="font-medium">
                  {receipt.donor_type === 'individual' ? 'Particulier' : 'Entreprise'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Nom :</span>
                <span className="font-medium">{receipt.donor_name}</span>
              </div>
              {receipt.donor_company_name && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Entreprise :</span>
                    <span className="font-medium">{receipt.donor_company_name}</span>
                  </div>
                  {receipt.donor_legal_form && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Forme juridique :</span>
                      <span className="font-medium">{receipt.donor_legal_form}</span>
                    </div>
                  )}
                  {receipt.donor_siren && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">SIREN :</span>
                      <span className="font-medium">{receipt.donor_siren}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Adresse :</span>
                <span className="font-medium text-right">
                  {receipt.donor_address_number} {receipt.donor_address_street}<br />
                  {receipt.donor_postal_code} {receipt.donor_city}<br />
                  {receipt.donor_country}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Don</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Date du don :</span>
                <span className="font-medium">
                  {new Date(receipt.donation_date).toLocaleDateString('fr-FR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Montant (versements) :</span>
                <span className="font-medium">{Number(receipt.donation_amount).toFixed(2)} €</span>
              </div>
              {Number(receipt.donation_nature_amount) > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dons en nature :</span>
                    <span className="font-medium">{Number(receipt.donation_nature_amount).toFixed(2)} €</span>
                  </div>
                  {receipt.donation_nature_description && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Description :</span>
                      <p className="mt-1 text-sm text-gray-700">{receipt.donation_nature_description}</p>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-900 font-semibold">Montant total :</span>
                <span className="text-lg font-bold text-blue-600">{totalAmount.toFixed(2)} €</span>
              </div>
              {receipt.donation_payment_method && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Forme des versements :</span>
                  <span className="font-medium">
                    {
                      {
                        cash: 'Remise d\'espèces',
                        check: 'Chèque',
                        transfer: 'Virement/CB',
                        other: 'Autre'
                      }[receipt.donation_payment_method]
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Organisme bénéficiaire</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Type :</span>
                <span className="font-medium">
                  {
                    {
                      association_loi_1901: 'Association loi 1901',
                      association_utilite_publique: 'Association reconnue d\'utilité publique',
                      fondation_entreprise: 'Fondation d\'entreprise',
                      association_cultuelle: 'Association cultuelle',
                      fonds_dotation: 'Fonds de dotation',
                      other: 'Autre'
                    }[receipt.organization_type] || receipt.organization_type
                  }
                </span>
              </div>
              {receipt.organization_other_details && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Détails :</span>
                  <span className="font-medium">{receipt.organization_other_details}</span>
                </div>
              )}
            </div>
          </div>

          {receipt.issue_date && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Émission</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date d'émission :</span>
                  <span className="font-medium">
                    {new Date(receipt.issue_date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                {receipt.issued_by && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Signataire :</span>
                    <span className="font-medium">{receipt.issued_by}</span>
                  </div>
                )}
                {receipt.signature_position && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fonction :</span>
                    <span className="font-medium">{receipt.signature_position}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {receipt.notes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Notes internes</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{receipt.notes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          {receipt.pdf_url && (
            <button
              onClick={() => window.open(receipt.pdf_url, '_blank')}
              className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger PDF
            </button>
          )}
          {receipt.status !== 'cancelled' && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Modifier
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
